/**
 * Mapping between the panel API wire shapes (docs/panel-api.md, snake_case)
 * and the exact props the hand-off components expect (camelCase).
 *
 * Two conversions are called out in docs/panel-decisions.md:
 *   1. the API speaks `content_mode`, the components speak `content`
 *      (`toComponentMode` / `toApiMode` — the single place this happens),
 *   2. `percentile` can be null (components render "—") and `diff_quality` is
 *      `Full` | `Reduced (no UID)`.
 */

import { failureRate } from "../ui/failureTone.js";

export const COMPONENT_MODE_CONTENT = "content";
export const COMPONENT_MODE_NOTIFY_ONLY = "notify_only";

const own = (obj, key) =>
  obj != null && Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined;

/** First defined value among `keys` (tolerates a camelCase variant too). */
export function pick(obj, ...keys) {
  for (const key of keys) {
    if (own(obj, key)) return obj[key];
  }
  return undefined;
}

export function list(value) {
  return Array.isArray(value) ? value : [];
}

export function num(value, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** Numbers that are allowed to stay null in the UI (percentile -> "—"). */
export function nullableNum(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/* ------------------------------------------------------------------ modes -- */

/** API value (`content_mode` | `content` | anything else) -> component value. */
export function toComponentMode(value) {
  return value === "content" || value === "content_mode"
    ? COMPONENT_MODE_CONTENT
    : COMPONENT_MODE_NOTIFY_ONLY;
}

/** Component value -> API body value. The API accepts both and normalises. */
export function toApiMode(value) {
  return value === COMPONENT_MODE_CONTENT ? COMPONENT_MODE_CONTENT : COMPONENT_MODE_NOTIFY_ONLY;
}

/**
 * NOTE: this reads `content_mode` first on purpose. In the channels response
 * `project.mode` carries the *polling* mode ("adaptive"), so never feed that
 * object through here — see `mapChannelPageProject`.
 */
export function projectDeliveryMode(project) {
  return toComponentMode(pick(project, "content_mode", "mode"));
}

export function toComponentDiffQuality(value) {
  if (value == null) return "Full";
  return /reduced/i.test(String(value)) ? "Reduced (no UID)" : "Full";
}

/* ------------------------------------------------------------------ plans -- */

export function mapPlan(dto) {
  if (!dto) return null;
  const usedFeeds = num(pick(dto, "used_feeds", "usedFeeds"));
  const rawTotal = pick(dto, "total_feeds", "totalFeeds");
  // An unlimited plan reports `null`; collapsing it to `usedFeeds` keeps the
  // usage bar at 100% instead of NaN%.
  const totalFeeds = rawTotal == null ? Math.max(usedFeeds, 1) : num(rawTotal, usedFeeds);
  return {
    id: pick(dto, "id"),
    name: pick(dto, "name") ?? "—",
    rateLabel: pick(dto, "rate_label", "rateLabel") ?? "",
    usedFeeds,
    totalFeeds,
    forecast: nullableNum(pick(dto, "forecast")),
    forecastDate: pick(dto, "forecast_date", "forecastDate") ?? null,
    feedLimit: pick(dto, "feed_limit", "feedLimit") ?? null,
  };
}

/* -------------------------------------------------------------- dashboard -- */

/**
 * mapLatency reads a response-time summary.
 *
 * The two numbers are null rather than 0 when nothing was measured, and the
 * difference matters on screen: "0 ms" beside a real average reads as a provider
 * that answered instantly, when the truth is that no poll was timed.
 */
export function mapLatency(dto) {
  if (!dto) return { samples: 0, avgMs: null, p95Ms: null };
  return {
    samples: num(pick(dto, "samples")),
    avgMs: nullableNum(pick(dto, "avg_ms", "avgMs")),
    p95Ms: nullableNum(pick(dto, "p95_ms", "p95Ms")),
  };
}

/**
 * mapProvider reads one provider row of a project.
 *
 * `filterable` is the server's answer, not a guess: the overflow row folding
 * several unrelated providers has no hostname behind it, so the panel renders it
 * without a link rather than offering a filter that would answer a question
 * nobody asked.
 */
export function mapProvider(dto) {
  if (!dto) return null;
  return {
    provider: pick(dto, "provider") ?? "—",
    feeds: num(pick(dto, "feeds")),
    changes: num(pick(dto, "changes")),
    polls: num(pick(dto, "polls")),
    changeRatio: nullableNum(pick(dto, "change_ratio", "changeRatio")),
    lastChangeLabel: pick(dto, "last_change_label", "lastChangeLabel") ?? "never",
    // The instant beside the label. The label is what a person reads; the
    // instant is what decides the colour, because "never" is not an age and
    // parsing "2h ago" back into hours would re-derive a fact we already had.
    lastChangedAt: pick(dto, "last_changed_at", "lastChangedAt") ?? "",
    filterable: pick(dto, "filterable") !== false,
  };
}

export function mapDashboardProject(dto) {
  if (!dto) return null;
  return {
    id: dto.id,
    name: pick(dto, "name") ?? "—",
    url: pick(dto, "callback_url", "url") ?? "",
    feedCount: num(pick(dto, "feed_count", "feedCount")),
    changes24h: num(pick(dto, "changes_24h", "changes24h")),
    mode: pick(dto, "mode") === "downtime" ? "downtime" : "active",
    providerCount: num(pick(dto, "provider_count", "providerCount")),
    providers: list(pick(dto, "providers")).map(mapProvider).filter(Boolean),
    responseTimes: mapLatency(pick(dto, "response_times", "responseTimes")),
  };
}

export function mapDashboard(dto) {
  const stats = dto?.stats ?? {};
  return {
    plan: mapPlan(dto?.plan),
    stats: {
      requests24h: num(pick(stats, "requests_24h", "requests24h")),
      changes24h: num(pick(stats, "changes_24h", "changes24h")),
      webhooksSent: num(pick(stats, "webhooks_sent", "webhooksSent")),
      failedDeliveries: num(pick(stats, "failed_deliveries", "failedDeliveries")),
      feeds: num(pick(stats, "feeds")),
      responseTimes: mapLatency(pick(stats, "response_times", "responseTimes")),
    },
    projects: list(dto?.projects).map(mapDashboardProject),
  };
}

/* --------------------------------------------------------------- projects -- */

/**
 * The two budget vocabularies, as the wire spells them.
 *
 * Named here rather than repeated as string literals: the last time the client
 * hard-coded "adaptive" the server renamed it, and every save from the panel
 * started failing with a 400 that said the mode was invalid. A single place that
 * maps the vocabulary is what stops that repeating.
 *
 * They are independent settings: `feed_budget_mode` is how ONE feed spends its
 * own day, `group_budget_mode` is how a project divides its allowance between
 * its feeds. Both happen to spell their two values the same way, so the two
 * coercers below are the whole difference between them.
 */
export const FEED_BUDGET_MODES = { fixed: "fixed", adaptive: "adaptive" };
export const GROUP_BUDGET_MODES = { fixed: "fixed", adaptive: "adaptive" };

/**
 * The schedule endpoint's mode labels, as the wire spells them.
 *
 * They describe where a feed's cadence actually comes from rather than restating
 * the project's settings: a pinned interval, an adaptive day the feed shaped out
 * of its own history, or an adaptive day whose size the group's distribution
 * decided. Only "fixed" is pinned; both adaptive flavours are adaptive, which is
 * why the screen branches on `fixed` and only then on which flavour it is.
 */
export const SCHEDULE_MODES = {
  fixed: "fixed",
  adaptive: "adaptive",
  adaptiveGroup: "adaptive_group",
};

/**
 * The group-split bounds the product ships with, mirroring the server's
 * DefaultMinBudget / DefaultMaxBudget. They are the quietest-feed and
 * busiest-feed bounds an adaptive group distributes between.
 *
 * Defined once and used both when reading a project and when writing one, so the
 * form, the mapper and the server cannot drift into three different pairs — which
 * is exactly what happened to the polling-mode vocabulary next door.
 */
export const DEFAULT_BUDGET_BOUNDS = { min: 10, max: 300 };

/** The hourly floor/ceiling an adaptive feed day ships with (server defaults). */
export const DEFAULT_PER_HOUR_BOUNDS = { min: 1, max: 15 };
/** Slider range for the hourly bounds: capped at the server's MaxFeedPerHour. */
export const PER_HOUR_LIMITS = { min: 1, max: 15 };
/** `feed_daily_budget`: 12 = one poll every two hours, 1440 = one every minute. */
export const FEED_DAILY_BUDGET_RANGE = { min: 12, max: 1440 };
/** No pinned budget yet: 96 polls a day, the old 15-minute default expressed in days. */
export const DEFAULT_FEED_DAILY_BUDGET = 96;

function modeOf(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

/** Feeds the reader did not pin a budget to read as adaptive, which is what they were. */
export function feedBudgetModeOf(value) {
  return modeOf(value) === FEED_BUDGET_MODES.fixed ? FEED_BUDGET_MODES.fixed : FEED_BUDGET_MODES.adaptive;
}

/** An unknown group split reads as fixed: the split that cannot starve a quiet feed. */
export function groupBudgetModeOf(value) {
  return modeOf(value) === GROUP_BUDGET_MODES.adaptive ? GROUP_BUDGET_MODES.adaptive : GROUP_BUDGET_MODES.fixed;
}

/** An unknown schedule mode reads as the plain adaptive day, never as "not polled". */
export function scheduleModeOf(value) {
  const mode = modeOf(value);
  if (mode === SCHEDULE_MODES.fixed) return SCHEDULE_MODES.fixed;
  if (mode === SCHEDULE_MODES.adaptiveGroup) return SCHEDULE_MODES.adaptiveGroup;
  return SCHEDULE_MODES.adaptive;
}

/**
 * projectLifecycle is the project's lifecycle status, which is a different axis
 * from `mode`.
 *
 * `mode` says *how* we deliver (ids only, or content) and where a scheduled
 * downtime falls; `status` says *whether* we deliver at all. The panel showed only
 * the first, so a project suspended by the webhook circuit breaker read as
 * "Active" while every notification was being held — which is how a customer can
 * lose a week without noticing. Anything that is not "active" is worth saying out
 * loud, so the screen renders it.
 */
function projectLifecycle(dto) {
  const status = pick(dto, "status");
  return status === "failing" || status === "suspended" ? status : "active";
}

export function mapProject(dto) {
  if (!dto) return null;
  return {
    id: dto.id,
    name: pick(dto, "name") ?? "",
    callbackUrl: pick(dto, "callback_url", "callbackUrl") ?? "",
    mode: projectDeliveryMode(dto),
    // How one feed spends its own day, and the pinned number of polls when it is
    // pinned. `feed_daily_budget` is only sent in fixed mode, so it stays null
    // rather than a stale number the next switch would resurrect.
    feedBudgetMode: feedBudgetModeOf(pick(dto, "feed_budget_mode", "feedBudgetMode")),
    feedDailyBudget: nullableNum(pick(dto, "feed_daily_budget", "feedDailyBudget")),
    // The floor and ceiling of an adaptive day. Sent for every feed mode (they
    // are the customer's), so the sliders show the stored pair the moment the
    // customer switches to adaptive rather than appearing to reset them.
    feedMinPerHour: num(pick(dto, "feed_min_per_hour", "feedMinPerHour"), DEFAULT_PER_HOUR_BOUNDS.min),
    feedMaxPerHour: num(pick(dto, "feed_max_per_hour", "feedMaxPerHour"), DEFAULT_PER_HOUR_BOUNDS.max),
    // How the project divides its allowance between feeds, and the bounds it
    // works between. Sent by the server for every mode.
    groupBudgetMode: groupBudgetModeOf(pick(dto, "group_budget_mode", "groupBudgetMode")),
    minBudget: num(pick(dto, "polling_min_budget", "pollingMinBudget"), DEFAULT_BUDGET_BOUNDS.min),
    maxBudget: num(pick(dto, "polling_max_budget", "pollingMaxBudget"), DEFAULT_BUDGET_BOUNDS.max),
    // The lifecycle status, normalised. A raw `status` used to sit here as well,
    // further down the same literal, so this value was silently overwritten by
    // whatever the server sent — including a null for a payload that omitted it.
    status: projectLifecycle(dto),
    // Deliveries are suspended for two different reasons and they need different
    // sentences: one is our circuit breaker reacting to an endpoint that stopped
    // answering, the other is an operator or a billing event. Deciding that in the
    // component would put the distinction in the wrong layer.
    deliveriesSuspended: projectLifecycle(dto) !== "active",
    createdAt: pick(dto, "created_at", "createdAt") ?? null,
  };
}

/** CreateProjectForm's `initialValues` (edit mode). */
export function mapProjectToFormValues(project) {
  if (!project) return undefined;
  return {
    name: project.name ?? "",
    callbackUrl: project.callbackUrl ?? "",
    mode: project.mode ?? COMPONENT_MODE_NOTIFY_ONLY,
    feedBudgetMode: feedBudgetModeOf(project?.feedBudgetMode),
    feedDailyBudget: project?.feedDailyBudget ?? DEFAULT_FEED_DAILY_BUDGET,
    feedMinPerHour: project?.feedMinPerHour ?? DEFAULT_PER_HOUR_BOUNDS.min,
    feedMaxPerHour: project?.feedMaxPerHour ?? DEFAULT_PER_HOUR_BOUNDS.max,
    groupBudgetMode: groupBudgetModeOf(project?.groupBudgetMode),
    // The bug this fixes: the bounds were never carried, so opening Edit seeded
    // the 10/300 defaults and the next PATCH wrote them back over the stored
    // pair — a customer's own bounds, silently discarded by a save that touched
    // something else entirely.
    minBudget: project?.minBudget ?? DEFAULT_BUDGET_BOUNDS.min,
    maxBudget: project?.maxBudget ?? DEFAULT_BUDGET_BOUNDS.max,
  };
}

/** CreateProjectForm `onSubmit` values -> `POST/PATCH /projects` body. */
export function toProjectBody(input = {}) {
  const feedBudgetMode = feedBudgetModeOf(input.feedBudgetMode);
  return {
    name: input.name,
    callback_url: input.callbackUrl,
    mode: toApiMode(input.mode),
    feed_budget_mode: feedBudgetMode,
    // A pinned budget only means anything in fixed mode; adaptive answers null so
    // the server never reads a stale number as a request for one.
    feed_daily_budget:
      feedBudgetMode === FEED_BUDGET_MODES.fixed
        ? num(input.feedDailyBudget, DEFAULT_FEED_DAILY_BUDGET)
        : null,
    // The hourly and group settings travel for every mode: they are the
    // customer's and survive a spell on a pinned budget, so a switch back does
    // not silently restore defaults.
    feed_min_per_hour: num(input.feedMinPerHour, DEFAULT_PER_HOUR_BOUNDS.min),
    feed_max_per_hour: num(input.feedMaxPerHour, DEFAULT_PER_HOUR_BOUNDS.max),
    group_budget_mode: groupBudgetModeOf(input.groupBudgetMode),
    polling_min_budget: num(input.minBudget, DEFAULT_BUDGET_BOUNDS.min),
    polling_max_budget: num(input.maxBudget, DEFAULT_BUDGET_BOUNDS.max),
  };
}

/**
 * toProjectPatch is the body for `PATCH /projects/{id}`: only the fields the
 * caller actually passed.
 *
 * Sending the whole object would be a *claim* about fields this caller knows
 * nothing about. That is not hypothetical: the settings screen saves the
 * webhook mode and the callback URL, and reusing the create-shaped body there
 * sent a budget mode on every save — silently re-shaping a project's polling the
 * customer had set, and (before the server learned to merge) failing outright
 * with "a project name is required" because the name was not part of the form
 * either.
 *
 * The server merges what it receives over the stored project, so an absent key
 * is the correct way to say "leave this alone".
 */
export function toProjectPatch(input = {}) {
  const patch = {};
  if (own(input, "name")) patch.name = input.name;
  if (own(input, "callbackUrl")) patch.callback_url = input.callbackUrl;
  if (own(input, "mode")) patch.mode = toApiMode(input.mode);
  if (own(input, "minBudget") || own(input, "maxBudget")) {
    patch.polling_min_budget = num(input.minBudget, DEFAULT_BUDGET_BOUNDS.min);
    patch.polling_max_budget = num(input.maxBudget, DEFAULT_BUDGET_BOUNDS.max);
  }
  if (own(input, "feedBudgetMode")) {
    const feedBudgetMode = feedBudgetModeOf(input.feedBudgetMode);
    patch.feed_budget_mode = feedBudgetMode;
    // A daily budget only means anything in fixed mode, and the server clears it
    // in adaptive mode, so sending one with an adaptive switch would be noise.
    if (feedBudgetMode === FEED_BUDGET_MODES.fixed) {
      patch.feed_daily_budget = num(input.feedDailyBudget, DEFAULT_FEED_DAILY_BUDGET);
    }
  } else if (own(input, "feedDailyBudget") && input.feedDailyBudget != null) {
    patch.feed_daily_budget = num(input.feedDailyBudget, DEFAULT_FEED_DAILY_BUDGET);
  }
  if (own(input, "feedMinPerHour")) patch.feed_min_per_hour = num(input.feedMinPerHour, DEFAULT_PER_HOUR_BOUNDS.min);
  if (own(input, "feedMaxPerHour")) patch.feed_max_per_hour = num(input.feedMaxPerHour, DEFAULT_PER_HOUR_BOUNDS.max);
  if (own(input, "groupBudgetMode")) patch.group_budget_mode = groupBudgetModeOf(input.groupBudgetMode);
  return patch;
}

/** 1440 / pollsPerDay as whole minutes: the gap a pinned daily budget buys. */
function minutesBetween(perDay) {
  return Math.max(1, Math.round(1440 / num(perDay, DEFAULT_FEED_DAILY_BUDGET)));
}

export function buildPollingSummary(project) {
  if (!project) return "";
  // The order mirrors the server's PollingSummary exactly: a pinned feed day is
  // the most concrete claim and wins, then a group that shares its allowance
  // out. Only the last case mentions a number, and it is the feed's own.
  if (project.feedBudgetMode === FEED_BUDGET_MODES.fixed) {
    return `fixed · every ${minutesBetween(project.feedDailyBudget)} min`;
  }
  if (project.groupBudgetMode === GROUP_BUDGET_MODES.adaptive) {
    return "adaptive · shared across the group";
  }
  // Adaptive feed under a fixed group split: the feed decides when to spend its
  // conserved day, which is the 96 polls the server reports.
  return `adaptive · ${num(project.feedDailyBudget, DEFAULT_FEED_DAILY_BUDGET)} polls/day`;
}

function mapWebhook(dto, project) {
  const source = dto ?? {};
  const hasMode = own(source, "content_mode") || own(source, "mode");
  return {
    callbackUrl: pick(source, "callback_url", "callbackUrl") ?? project?.callbackUrl ?? "",
    mode: hasMode ? toComponentMode(pick(source, "content_mode", "mode")) : project?.mode ?? COMPONENT_MODE_NOTIFY_ONLY,
  };
}

/** `GET /projects/{id}` -> everything the settings screen needs. */
export function mapProjectDetail(dto) {
  const project = mapProject(dto?.project);
  return {
    ...project,
    apiKey: pick(dto, "api_key", "apiKey") ?? "",
    webhook: mapWebhook(dto?.webhook, project),
    polling: dto?.polling ?? null,
    plan: mapPlan(dto?.plan),
    // The same header the dashboard's project card shows, from the same read:
    // the project screen is that card opened, not a second opinion about it.
    // Off the mapped project rather than through pick(): pick reads its first
    // argument, and a detail response with no project would hand it null.
    callbackUrl: project?.callbackUrl ?? "",
    providerCount: num(pick(dto, "provider_count", "providerCount")),
    providers: list(pick(dto, "providers")).map(mapProvider).filter(Boolean),
    responseTimes: mapLatency(pick(dto, "response_times", "responseTimes")),
  };
}

/* --------------------------------------------------------------- activity -- */

/**
 * The project screen's activity window: how many requests we made and how many
 * changes we detected, hour by hour.
 *
 * The two series are different magnitudes by two orders — a feed is polled dozens of
 * times a day and changes a handful of times a month — so the chart draws them
 * against two axes. Everything here is passed through unchanged rather than
 * recomputed: a client that re-derived a bucket would eventually disagree with the
 * server about the customer's numbers.
 */
export function mapProjectActivity(dto) {
  return {
    project: {
      id: pick(dto?.project, "id") ?? null,
      name: pick(dto?.project, "name") ?? "",
    },
    hours: num(pick(dto, "hours"), 24),
    from: pick(dto, "from") ?? null,
    to: pick(dto, "to") ?? null,
    buckets: list(dto?.buckets).map((bucket) => ({
      at: pick(bucket, "at") ?? null,
      requests: num(pick(bucket, "requests")),
      // A subset of `requests`, not a third series alongside it: the badge above
      // the chart reads it as a share of the requests in the same window.
      failed: num(pick(bucket, "failed")),
      changes: num(pick(bucket, "changes")),
      // The hour in progress, marked by the server: a half-filled bar that is not
      // labelled reads as a drop in traffic.
      partial: Boolean(pick(bucket, "partial")),
    })),
    totals: {
      requests: num(pick(dto?.totals, "requests")),
      failed: num(pick(dto?.totals, "failed")),
      changes: num(pick(dto?.totals, "changes")),
    },
    generatedAt: pick(dto, "generated_at", "generatedAt") ?? null,
  };
}

/* ------------------------------------------------------------ reliability -- */

/**
 * The failure-share report: everything, by provider, by feed.
 *
 * The rate is read from the server when it is there and recomputed from the two
 * counters when it is not. Both paths use the same helper the request badge uses,
 * so a share can never disagree with the counts printed next to it — which is the
 * one thing a reader will check.
 *
 * `measurable` is passed through rather than derived from `minPolls`: the server
 * owns the floor, reports it, and a client that recomputed the rule would keep
 * the old one after the server changed.
 */
export function mapReliabilityReport(dto) {
  const counters = (source) => {
    const polls = num(pick(source, "polls"));
    const failures = num(pick(source, "failures"));
    return {
      polls,
      failures,
      failureRate: num(pick(source, "failure_rate", "failureRate"), failureRate(failures, polls)),
    };
  };

  return {
    window: pick(dto, "window") ?? "24h",
    since: pick(dto, "since") ?? null,
    until: pick(dto, "until") ?? null,
    minPolls: num(pick(dto, "min_polls", "minPolls")),
    total: {
      ...counters(dto?.total),
      feeds: num(pick(dto?.total, "feeds")),
    },
    providers: list(dto?.providers).map((provider) => ({
      provider: pick(provider, "provider") ?? "",
      feeds: num(pick(provider, "feeds")),
      measurable: pick(provider, "measurable") !== false,
      ...counters(provider),
    })),
    feeds: list(dto?.feeds).map((feed) => ({
      feedId: pick(feed, "feed_id", "feedId") ?? null,
      provider: pick(feed, "provider") ?? "",
      url: pick(feed, "ical_url", "url") ?? "",
      healthStatus: pick(feed, "health_status", "healthStatus") ?? "",
      // Only filled for the admin scope: with no project filter, "whose feed is
      // this" is answered by how many projects watch it.
      projects: num(pick(feed, "projects")),
      measurable: pick(feed, "measurable") !== false,
      ...counters(feed),
    })),
    feedsMeasurable: num(pick(dto, "feeds_measurable", "feedsMeasurable")),
    feedsUnmeasurable: num(pick(dto, "feeds_unmeasurable", "feedsUnmeasurable")),
    generatedAt: pick(dto, "generated_at", "generatedAt") ?? null,
    project: dto?.project
      ? { id: pick(dto.project, "id") ?? null, name: pick(dto.project, "name") ?? "" }
      : null,
  };
}

/* ------------------------------------------------------- alert thresholds -- */

/**
 * The failure-rate threshold settings: the rules a project has, the bounds the
 * server will accept, and what a new rule starts as.
 *
 * The limits come from the server rather than being written into the form. They
 * are a property of the storage — the counters are hourly, so a window shorter
 * than an hour cannot be answered — and a client that hardcoded them would keep
 * offering a window the server refuses after the two drifted apart.
 */
export function mapAlertSettings(dto) {
  const limits = pick(dto, "limits") ?? {};
  const defaults = pick(dto, "defaults") ?? {};
  return {
    rules: list(dto?.rules).map((rule) => ({
      scope: pick(rule, "scope") ?? "project",
      windowHours: num(pick(rule, "window_hours", "windowHours"), 6),
      warningPercent: num(pick(rule, "warning_percent", "warningPercent")),
      errorPercent: num(pick(rule, "error_percent", "errorPercent")),
      minPolls: num(pick(rule, "min_polls", "minPolls")),
      enabled: pick(rule, "enabled") !== false,
    })),
    limits: {
      minWindowHours: num(pick(limits, "min_window_hours", "minWindowHours"), 1),
      maxWindowHours: num(pick(limits, "max_window_hours", "maxWindowHours"), 10),
      minMinPolls: num(pick(limits, "min_min_polls", "minMinPolls"), 2),
      maxMinPolls: num(pick(limits, "max_min_polls", "maxMinPolls"), 96),
      scopes: list(pick(limits, "scopes")),
    },
    defaults: {
      windowHours: num(pick(defaults, "window_hours", "windowHours"), 6),
      warningPercent: num(pick(defaults, "warning_percent", "warningPercent")),
      errorPercent: num(pick(defaults, "error_percent", "errorPercent")),
      minPolls: num(pick(defaults, "min_polls", "minPolls"), 5),
      enabled: pick(defaults, "enabled") !== false,
    },
  };
}

/**
 * toAlertRuleBody turns a form's rule into the request body.
 *
 * Every field is sent explicitly, including the ones the form left at zero: an
 * omitted `warning_percent` would mean "unchanged" to a whole-object API, and the
 * customer switching a warning off is sending exactly that zero. There is no ref:
 * a rule names a scope, and "per provider" is judged for every provider.
 */
export function toAlertRuleBody(rule) {
  return {
    scope: rule.scope,
    window_hours: Number(rule.windowHours),
    warning_percent: Number(rule.warningPercent),
    error_percent: Number(rule.errorPercent),
    min_polls: Number(rule.minPolls),
    enabled: Boolean(rule.enabled),
  };
}

/* --------------------------------------------------------------- channels -- */

export function mapChannel(dto) {
  if (!dto) return null;
  return {
    // `id` is this project's channel (the subscription); `feedId` is the
    // calendar behind it. Opening a feed needs the latter — the detail endpoint
    // is keyed on the feed, so passing the subscription id 404s.
    id: dto.id,
    feedId: pick(dto, "feed_id", "feedId") ?? null,
    externalId: pick(dto, "external_id", "externalId") ?? "",
    url: pick(dto, "url") ?? "",
    // The calendar service this row's URL belongs to, stored on the feed and
    // returned by the listing. Empty when the address named none.
    provider: pick(dto, "provider") ?? "",
    lastChangeLabel: pick(dto, "last_change_label", "lastChangeLabel") ?? "—",
    // null is meaningful: not enough history yet -> the component renders "—".
    percentile: nullableNum(pick(dto, "percentile")),
    unreachable: Boolean(pick(dto, "unreachable")),
  };
}

/**
 * The `project` object inside the channels response is NOT the project shape:
 * its `mode` is the polling mode (see the docs example: "mode":"adaptive").
 * Only name / feed_count / polling_summary are safe to read from it.
 */
export function mapChannelPageProject(dto) {
  if (!dto) return { name: null, feedCount: 0, pollingSummary: null };
  return {
    name: pick(dto, "name") ?? null,
    feedCount: num(pick(dto, "feed_count", "feedCount")),
    pollingSummary: pick(dto, "polling_summary", "pollingSummary") ?? null,
  };
}

export function mapChannelPage(dto) {
  return {
    project: mapChannelPageProject(dto?.project),
    channels: list(dto?.channels).map(mapChannel),
    total: num(pick(dto, "total"), list(dto?.channels).length),
    limit: num(pick(dto, "limit"), 50),
    offset: num(pick(dto, "offset"), 0),
    hasMore: Boolean(pick(dto, "has_more", "hasMore")),
  };
}

/* --------------------------------------------------------------- schedule -- */

/**
 * The polling schedule is only meaningful for a fixed interval: an adaptive feed
 * derives its cadence from its own change history, so there is no list of times
 * to promise. `mode` is what the screen branches on, and an unknown mode is
 * treated as adaptive — never as a schedule with no entries, which would read as
 * "this feed is not polled at all".
 */
export function mapFeedSchedule(dto) {
  // Only "fixed" is a pinned grid; both adaptive flavours are adaptive. The
  // flavour is kept rather than collapsed because it is the difference between
  // the feed shaping its own day and the group deciding how big that day is —
  // and the screen has copy for each.
  const mode = scheduleModeOf(pick(dto, "mode"));
  const fixed = mode === SCHEDULE_MODES.fixed;
  return {
    feed: {
      internalId: pick(dto?.feed, "internal_id", "internalId") ?? null,
      projectName: pick(dto?.feed, "project_name", "projectName") ?? "",
      externalId: pick(dto?.feed, "external_id", "externalId") ?? "",
    },
    mode,
    intervalMinutes: fixed ? nullableNum(pick(dto, "interval_minutes", "intervalMinutes")) : null,
    nextPollAt: fixed ? (pick(dto, "next_poll_at", "nextPollAt") ?? null) : null,
    overdue: fixed ? Boolean(pick(dto, "overdue")) : false,
    // A poll in flight keeps the anchor in the past by design, so the server
    // reports it separately from lateness; the screen must not call a feed
    // overdue once per poll.
    polling: fixed ? Boolean(pick(dto, "polling")) : false,
    // Both halves of the chart, in both modes. A fixed feed gets the exact grid
    // it has committed to; an adaptive one gets the simulation the server drew
    // from the feed's change history. Dropping the adaptive list here would
    // throw away the only thing the schedule tab has to show for it.
    polls: list(dto?.polls),
    // What actually happened inside the recent window, from the request log.
    // Distinct from `polls` because intent and record are different things.
    recent: list(dto?.recent),
    unreachable: Boolean(pick(dto, "unreachable")),
    pendingConfirmation: Boolean(pick(dto, "pending_confirmation", "pendingConfirmation")),
    generatedAt: pick(dto, "generated_at", "generatedAt") ?? null,
  };
}

/**
 * The request trail: one entry per outbound poll, newest first.
 *
 * `succeeded` comes from the server rather than being derived here, so the
 * definition of "the request worked" lives in one place. A 304 is a success and
 * is kept distinguishable from a 200, because "the provider confirmed nothing
 * changed" and "we downloaded the calendar again" cost very different amounts.
 */
export function mapPollAttempt(dto) {
  if (!dto) return null;
  return {
    at: pick(dto, "at") ?? null,
    statusCode: num(pick(dto, "status_code", "statusCode")),
    notModified: Boolean(pick(dto, "not_modified", "notModified")),
    durationMs: num(pick(dto, "duration_ms", "durationMs")),
    succeeded: Boolean(pick(dto, "succeeded")),
    error: pick(dto, "error") ?? "",
    // Whether the request found something. A 200 that changed nothing and a 200
    // that found three changed events are the same response and very different
    // answers to the question this trail is read for.
    changed: Boolean(pick(dto, "changed")),
    eventsChanged: num(pick(dto, "events_changed", "eventsChanged")),
    // The calendar's metadata moved and no entry did: not a change (the server
    // never notifies for it), but not a quiet poll either — without this the row
    // would show nothing for a poll whose hash actually moved.
    metadataChanged: Boolean(pick(dto, "metadata_changed", "metadataChanged")),
    metadataFields: list(pick(dto, "metadata_fields", "metadataFields")).map(String),
  };
}

export function mapPollLog(dto) {
  return {
    feed: {
      internalId: pick(dto?.feed, "internal_id", "internalId") ?? null,
      projectName: pick(dto?.feed, "project_name", "projectName") ?? "",
      externalId: pick(dto?.feed, "external_id", "externalId") ?? "",
    },
    attempts: list(dto?.attempts).map(mapPollAttempt),
    maxAttempts: num(pick(dto, "max_attempts", "maxAttempts"), 0),
    maxAgeHours: num(pick(dto, "max_age_hours", "maxAgeHours"), 0),
    generatedAt: pick(dto, "generated_at", "generatedAt") ?? null,
  };
}

/* --------------------------------------------------- feed content (dev aid) -- */

/**
 * One calendar entry as the server rendered it for a person.
 *
 * The server sends phrases, not raw iCalendar: `when` is already
 * "Fri 12 Jul 2026, 14:00–16:00 UTC". The component must not re-parse or
 * re-format them, or the screen and the payload the customer received could
 * disagree about the same event.
 */
export function mapEventView(dto) {
  if (!dto) return null;
  return {
    uid: pick(dto, "uid") ?? "",
    title: pick(dto, "title") ?? "",
    when: pick(dto, "when") ?? "",
    startsAt: pick(dto, "starts_at", "startsAt") ?? null,
    endsAt: pick(dto, "ends_at", "endsAt") ?? null,
    allDay: Boolean(pick(dto, "all_day", "allDay")),
    where: pick(dto, "where") ?? "",
    status: pick(dto, "status") ?? "",
    details: pick(dto, "details") ?? "",
  };
}

/** One event a poll stopped seeing, with when we noticed. */
export function mapFeedRemoval(dto) {
  if (!dto) return null;
  return {
    detectedAt: pick(dto, "detected_at", "detectedAt") ?? null,
    event: mapEventView(pick(dto, "event")),
  };
}

/**
 * The content-inspection response.
 *
 * `available` and `reason` travel together on purpose: an empty entry list has
 * three very different causes — notify_only mode, a snapshot that has not been
 * written yet, and an empty calendar — and a screen that rendered all three as
 * "no events" would be lying about two of them.
 */
export function mapFeedContent(dto) {
  return {
    feedId: pick(dto, "feed_id", "feedId") ?? null,
    projectName: pick(dto, "project_name", "projectName") ?? "",
    externalId: pick(dto, "external_id", "externalId") ?? "",
    mode: toComponentMode(pick(dto, "mode")) ?? "",
    available: Boolean(pick(dto, "available")),
    reason: pick(dto, "reason") ?? "",
    capturedAt: pick(dto, "captured_at", "capturedAt") ?? null,
    entries: list(dto?.entries).map(mapEventView),
    removed: list(dto?.removed).map(mapFeedRemoval),
    retention: {
      contentHours: num(pick(dto?.retention, "content_hours", "contentHours")),
      removalsHours: num(pick(dto?.retention, "removals_hours", "removalsHours")),
      removalsCapacity: num(pick(dto?.retention, "removals_capacity", "removalsCapacity")),
    },
    notice: pick(dto, "notice") ?? "",
    generatedAt: pick(dto, "generated_at", "generatedAt") ?? null,
  };
}

/* ------------------------------------------------------------- deliveries -- */

export function mapDelivery(dto) {
  if (!dto) return null;
  return {
    id: dto.id,
    time: pick(dto, "time", "occurred_at") ?? "",
    feedExternalId: pick(dto, "feed_external_id", "feedExternalId") ?? "",
    attempts: num(pick(dto, "attempts")),
    maxAttempts: num(pick(dto, "max_attempts", "maxAttempts")),
    status: pick(dto, "status") ?? "failed",
  };
}

export function mapActiveRetry(dto) {
  if (!dto) return null;
  return {
    feedExternalId: pick(dto, "feed_external_id", "feedExternalId") ?? "",
    events: list(dto.events).map((event) => ({
      label: pick(event, "label") ?? "",
      elapsed: pick(event, "elapsed") ?? "",
      state: pick(event, "state") ?? "pending",
    })),
    nextAttemptLabel: pick(dto, "next_attempt_label", "nextAttemptLabel") ?? "",
    attempt: num(pick(dto, "attempt")),
    maxAttempts: num(pick(dto, "max_attempts", "maxAttempts")),
  };
}

export function mapDeliveryPage(dto) {
  return {
    project: {
      name: pick(dto?.project, "name") ?? "",
      callbackUrl: pick(dto?.project, "callback_url", "callbackUrl") ?? "",
    },
    deliveries: list(dto?.deliveries).map(mapDelivery),
    activeRetry: mapActiveRetry(pick(dto, "active_retry", "activeRetry")),
    payloadPreview: pick(dto, "payload_preview", "payloadPreview") ?? null,
    total: num(pick(dto, "total"), list(dto?.deliveries).length),
    limit: num(pick(dto, "limit"), 50),
    offset: num(pick(dto, "offset"), 0),
    hasMore: Boolean(pick(dto, "has_more", "hasMore")),
  };
}

/* --------------------------------------------------------------- downtime -- */

export function mapDowntimeWindow(dto) {
  if (!dto) return null;
  return {
    id: dto.id,
    label: pick(dto, "label") ?? "",
    status: pick(dto, "status") ?? "",
    startsAt: pick(dto, "starts_at", "startsAt") ?? null,
    endsAt: pick(dto, "ends_at", "endsAt") ?? null,
  };
}

export function mapDowntimeList(dto) {
  return { windows: list(dto?.windows).map(mapDowntimeWindow) };
}

/* ------------------------------------------------------------------ feeds -- */

/**
 * hourSeries reads one 24-bucket hour-of-day series off the wire.
 *
 * It returns `undefined` rather than an array of zeros when the server sent
 * nothing, because "this deployment does not report the series" and "the series
 * is zero in every hour" are different facts and the chart renders them
 * differently: an absent plan is a chart with no bars to draw, a zero plan is a
 * plan that spends nothing. A number the server sent as null becomes zero — that
 * is a bucket, not a missing series.
 */
function hourSeries(value) {
  const series = list(value).map((entry) => num(entry, 0));
  return series.length ? series : undefined;
}

export function mapFeedDetail(dto) {
  const feed = dto?.feed ?? {};
  return {
    feed: {
      projectName: pick(feed, "project_name", "projectName") ?? "",
      externalId: pick(feed, "external_id", "externalId") ?? "",
      internalId: pick(feed, "internal_id", "internalId") ?? "",
      active: Boolean(pick(feed, "active")),
    },
    // null -> FeedDetail renders "—" (see the one-line fix in that component).
    percentile: nullableNum(pick(dto, "percentile")),
    currentPollRate: pick(dto, "current_poll_rate", "currentPollRate") ?? "—",
    baselineRate: pick(dto, "baseline_rate", "baselineRate") ?? "—",
    diffQuality: toComponentDiffQuality(pick(dto, "diff_quality", "diffQuality")),
    // Retained for clients that only know the normalised shape; the chart now
    // reads the absolute series below, which can say "four polls in this hour"
    // where a share of the busiest hour could not.
    hourlyWeights: hourSeries(pick(dto, "hourly_weights", "hourlyWeights")),
    // The three hour-of-day series the chart is drawn from, all in the same
    // bucket order and the same zone as hourly_weights: the plan (absolute polls
    // per hour, the same allocation the schedule endpoint simulates), today's
    // actual counters, and the changed events the plan is shaped from.
    plannedPollsByHour: hourSeries(pick(dto, "planned_polls_by_hour", "plannedPollsByHour")),
    observedPollsByHour: hourSeries(pick(dto, "observed_polls_by_hour", "observedPollsByHour")),
    changedEventsByHour: hourSeries(pick(dto, "changed_events_by_hour", "changedEventsByHour")),
    // The zone those 24 buckets were recorded in, and its offset at request
    // time. The chart is an hour *index*, so without this the screen would have
    // to assume the buckets are the reader's — which they are not, because a
    // feed is shared and the engine schedules it in one wall clock.
    histogramTimezone: pick(dto, "histogram_timezone", "histogramTimezone") ?? "UTC",
    histogramUtcOffsetMinutes: num(
      pick(dto, "histogram_utc_offset_minutes", "histogramUtcOffsetMinutes"),
      0,
    ),
    recentChanges: list(pick(dto, "recent_changes", "recentChanges")).map((change) => ({
      timestamp: pick(change, "timestamp") ?? "",
      description: pick(change, "description") ?? "",
      status: pick(change, "status") ?? "",
    })),
    // Not part of the documented /feeds/{id} payload yet — read defensively so
    // the banners light up the moment the columns from panel-decisions 3.12 land.
    unreachable: Boolean(pick(dto, "unreachable", "feed_unreachable")),
    unreachableHours: nullableNum(pick(dto, "unreachable_hours", "hours_down")),
    pendingConfirmationAt:
      pick(dto, "pending_confirmation_at", "pendingConfirmationAt") ?? null,
    massDeletionPending: Boolean(pick(dto, "mass_deletion_pending", "massDeletionPending")),
  };
}

/* ------------------------------------------------------------ preferences -- */

export function mapNotificationRows(dto) {
  return {
    rows: list(dto?.rows).map((row) => ({
      key: pick(row, "key") ?? "",
      label: pick(row, "label") ?? "",
      email: Boolean(pick(row, "email")),
      sms: Boolean(pick(row, "sms")),
    })),
  };
}

/* ---------------------------------------------------------------- billing -- */

export function mapInvoice(dto) {
  if (!dto) return null;
  return {
    id: dto.id,
    periodLabel: pick(dto, "period_label", "periodLabel") ?? "",
    amountLabel: pick(dto, "amount_label", "amountLabel") ?? "",
    status: pick(dto, "status") ?? "",
  };
}

/**
 * mapPrice is one interval's price on a plan: what the provider will charge, or
 * an explicit "this deployment does not sell it".
 *
 * The amount and its label come from the server, which read them from Stripe, so
 * what the customer is shown is what they will be charged. `available: false` is
 * a state to render, not a missing field to guess at — the period screen says the
 * option is unavailable rather than inventing a number.
 *
 * discountPercent, savingLabel and fullPriceLabel are present only when paying
 * for a year is actually cheaper than twelve months; all three are computed
 * server-side so the advertised saving cannot outlive the prices behind it.
 */
export function mapPrice(dto) {
  if (!dto) return { available: false };
  return {
    available: Boolean(pick(dto, "available")),
    amountCents: nullableNum(pick(dto, "amount_cents", "amountCents")),
    currency: pick(dto, "currency") ?? null,
    amountLabel: pick(dto, "amount_label", "amountLabel") ?? "",
    discountPercent: num(pick(dto, "discount_percent", "discountPercent")),
    savingLabel: pick(dto, "saving_label", "savingLabel") ?? null,
    fullPriceLabel: pick(dto, "full_price_label", "fullPriceLabel") ?? null,
  };
}

export function mapPlanOption(dto) {
  if (!dto) return null;
  return {
    id: dto.id,
    name: pick(dto, "name") ?? "—",
    feedLimit: nullableNum(pick(dto, "feed_limit", "feedLimit")),
    rateLabel: pick(dto, "rate_label", "rateLabel") ?? "",
    // contactOnly belongs to the tier (arranged with sales), purchasable to this
    // deployment (a Stripe price exists). The screen needs both: a contact-only
    // tier gets "Contact us" even when Stripe is configured, and a paid tier
    // with no price gets "Unavailable" rather than a button that cannot work.
    contactOnly: Boolean(pick(dto, "contact_only", "contactOnly")),
    purchasable: Boolean(pick(dto, "purchasable")),
    current: Boolean(pick(dto, "current")),
    // What each billing period costs. Both keys are always present, so the period
    // screen reads a state instead of inferring one from an absent field.
    prices: {
      monthly: mapPrice(dto.prices?.monthly),
      yearly: mapPrice(dto.prices?.yearly),
    },
  };
}

/**
 * A plan change has two possible outcomes and they are not interchangeable: a
 * plan with no price switches locally and answers with the new plan, while a
 * plan the provider can charge for answers with a hosted checkout and *no*
 * plan, because nothing has been paid for yet. Collapsing the two would let the
 * screen announce "Plan updated" while nothing had been charged.
 */
export function mapPlanChange(dto) {
  return {
    plan: dto?.plan ? mapPlan(dto.plan) : null,
    pending: Boolean(pick(dto, "pending")),
    checkoutUrl: pick(dto, "checkout_url", "checkoutUrl") ?? null,
  };
}

/** The provider's hosted billing portal, which is where a card is changed. */
export function mapPortal(dto) {
  return { portalUrl: pick(dto, "portal_url", "portalUrl") ?? null };
}

export function mapPaymentMethod(dto) {
  if (!dto) return null;
  return {
    brand: pick(dto, "brand") ?? "—",
    last4: pick(dto, "last4") ?? "————",
    expiry: pick(dto, "expiry") ?? "—",
  };
}

export function mapBilling(dto) {
  return {
    plan: mapPlan(dto?.plan),
    paymentMethod: mapPaymentMethod(pick(dto, "payment_method", "paymentMethod")),
    // A deployment-wide fact, not an account one: a customer with no card on
    // file is normal, a server with no provider is not. Inferring the second
    // from the first is what told every customer the server could not charge.
    paymentsConfigured: Boolean(
      pick(dto, "payment_provider_configured", "paymentsConfigured", "paymentProviderConfigured"),
    ),
    invoices: list(dto?.invoices).map(mapInvoice),
    plans: list(dto?.plans).map(mapPlanOption),
    // The contact-only plan's destination comes from the server so the screen
    // never has to hardcode an address.
    contactUrl: pick(dto, "contact_url", "contactUrl") ?? null,
    // The deployment's own currency, for labelling the plan list. A price keeps
    // the currency its own Stripe price carries.
    currency: pick(dto, "currency") ?? null,
  };
}

/* ------------------------------------------------------------------ admin -- */

export function mapAdminMetrics(dto) {
  return {
    totalCustomers: num(pick(dto, "total_customers", "totalCustomers")),
    feedsTracked: num(pick(dto, "feeds_tracked", "feedsTracked")),
    queueDepth: num(pick(dto, "queue_depth", "queueDepth")),
    failedDeliveries1h: num(pick(dto, "failed_deliveries_1h", "failedDeliveries1h")),
  };
}

export function mapAdminCustomer(dto) {
  if (!dto) return null;
  return {
    id: dto.id,
    account: pick(dto, "account") ?? "",
    plan: pick(dto, "plan") ?? "",
    feedCount: num(pick(dto, "feed_count", "feedCount")),
    projectCount: num(pick(dto, "project_count", "projectCount")),
    status: pick(dto, "status") ?? "ok",
  };
}

export function mapAdminCustomerPage(dto) {
  return {
    customers: list(dto?.customers).map(mapAdminCustomer),
    total: num(pick(dto, "total"), list(dto?.customers).length),
    limit: num(pick(dto, "limit"), 50),
    offset: num(pick(dto, "offset"), 0),
    hasMore: Boolean(pick(dto, "has_more", "hasMore")),
  };
}

export function mapAdminAuditEntry(dto) {
  if (!dto) return null;
  return {
    at: pick(dto, "at") ?? "",
    actor: pick(dto, "actor") ?? "",
    action: pick(dto, "action") ?? "",
    target: pick(dto, "target") ?? "",
    detail: pick(dto, "detail") ?? "",
  };
}

export function mapAdminAudit(dto) {
  return { entries: list(dto?.entries).map(mapAdminAuditEntry) };
}

/* --------------------------------------------------------------- support -- */

/**
 * The help form's accepted answer.
 *
 * The endpoint answers 202 with an explicit status rather than an empty body, so
 * the screen can tell "we took it" from a state it does not yet understand. Any
 * other status maps to `accepted: false`, which the screen renders as a failure:
 * a 2xx we cannot read is not a message delivered, and saying it was would be
 * the one lie this form must never tell.
 */
export function mapContactSubmission(dto) {
  return { accepted: pick(dto, "status") === "accepted" };
}
